import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { usePrefersReducedMotion } from '../hooks/useMedia';
const CALENDLY_URL = 'https://calendly.com/moses0080098/30min';
const CALENDLY_SCRIPT = 'https://assets.calendly.com/assets/external/widget.js';
type BookingContextValue = {
  isOpen: boolean;
  openBooking: () => void;
  closeBooking: () => void;
};
const BookingContext = createContext<BookingContextValue | null>(null);

/** Opens the Calendly consultation scheduler from anywhere in the app. */
export function useBooking(): BookingContextValue {
  const context = useContext(BookingContext);
  if (!context) throw new Error('useBooking must be used inside <BookingProvider>');
  return context;
}
function loadCalendly(): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${CALENDLY_SCRIPT}"]`);
  if (existing) {
    if (existing.dataset.loaded === 'true') return Promise.resolve();
    return new Promise(resolve => existing.addEventListener('load', () => resolve(), {
      once: true
    }));
  }
  return new Promise(resolve => {
    const script = document.createElement('script');
    script.src = CALENDLY_SCRIPT;
    script.async = true;
    script.addEventListener('load', () => {
      script.dataset.loaded = 'true';
      resolve();
    }, {
      once: true
    });
    document.head.appendChild(script);
  });
}
function CalendlyEmbed() {
  const hostRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadCalendly().then(() => {
      if (cancelled || !hostRef.current) return;
      const calendly = (window as unknown as {
        Calendly?: {
          initInlineWidget: (options: {
            url: string;
            parentElement: HTMLElement;
          }) => void;
        };
      }).Calendly;
      hostRef.current.innerHTML = '';
      calendly?.initInlineWidget({
        url: CALENDLY_URL,
        parentElement: hostRef.current
      });
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return <div className="relative" data-id="element-50" data-mp="kpn9hf8y87to">
      {!ready ? <p className="absolute inset-x-0 top-24 text-center text-sm text-muted" role="status" data-id="element-51" data-mp="fty610tt66s3">
          Loading available times…
        </p> : null}
      <div ref={hostRef} className="calendly-inline-widget w-full" style={{
      minWidth: 320,
      height: 700
    }} data-url={CALENDLY_URL} data-id="element-52" data-mp="18hklb6ceezus" />
    </div>;
}
export function BookingProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const reduced = usePrefersReducedMotion();
  const openBooking = useCallback(() => setIsOpen(true), []);
  const closeBooking = useCallback(() => setIsOpen(false), []);
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);
  const value = useMemo(() => ({
    isOpen,
    openBooking,
    closeBooking
  }), [isOpen, openBooking, closeBooking]);
  return <BookingContext.Provider value={value} data-id="element-53" data-mp="2mg4aeb4dsrno">
      {children}
      {typeof document !== 'undefined' ? createPortal(<AnimatePresence data-id="element-54" data-mp="2rh1n486mgths">
              {isOpen ? <motion.div className="fixed inset-0 z-[120] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm sm:p-8" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} onClick={closeBooking} role="dialog" aria-modal="true" aria-label="Book a consultation" data-id="element-55" data-mp="2wfleio3zixhj">
                  <motion.div className="relative w-full max-w-3xl border border-line bg-surface" initial={reduced ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={reduced ? {
          opacity: 0
        } : {
          opacity: 0,
          y: 16
        }} transition={{
          duration: 0.4,
          ease: [0.16, 1, 0.3, 1]
        }} onClick={event => event.stopPropagation()} data-id="element-56" data-mp="31iki9hwtfpj4">
                    <div className="flex items-center justify-between border-b border-line px-5 py-4" data-id="element-57" data-mp="2dsgos3dcd65u">
                      <h2 className="label text-muted" data-id="element-58" data-mp="1q2mftww581qv">Book a Consultation</h2>
                      <button type="button" onClick={closeBooking} aria-label="Close booking" className="flex h-9 w-9 items-center justify-center border border-line text-ink transition-colors hover:border-brand hover:text-brand" data-id="element-59" data-mp="1m359z2ct12l6">
                        <XIcon className="h-4 w-4" data-id="element-60" data-mp="11y4q4ykf82vg" />
                      </button>
                    </div>
                    <CalendlyEmbed data-id="element-61" data-mp="16wzh4nsjkxqn" />
                  </motion.div>
                </motion.div> : null}
            </AnimatePresence>, document.body) : null}
    </BookingContext.Provider>;
}